import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import en from '../i18n/en.json'
import ptBR from '../i18n/pt-BR.json'
import { demoTranslations } from '../lib/demoClient'

const I18nContext = createContext({})

// In the demo the entity labels read as Meridian (src/lib/demoBrand.js);
// outside it demoTranslations returns the very same objects.
const TRANSLATIONS = { en: demoTranslations(en), 'pt-BR': demoTranslations(ptBR) }
const STORAGE_KEY = 'chatrone-lang'

// Portuguese-speaking country codes
const PT_COUNTRIES = ['BR', 'PT', 'AO', 'MZ', 'CV', 'GW', 'ST', 'TL']
// English-default country codes
const EN_COUNTRIES = ['US', 'GB', 'IE', 'AU', 'NZ', 'CA']

function getNestedValue(obj, path) {
  return path.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : null), obj)
}

async function detectCountry() {
  try {
    // Use timezone-based detection (no API call, no CORS issues)
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    if (tz.startsWith('America/Sao_Paulo') || tz.startsWith('America/Fortaleza') ||
        tz.startsWith('America/Recife') || tz.startsWith('America/Bahia') ||
        tz.startsWith('America/Belem') || tz.startsWith('America/Manaus') ||
        tz.startsWith('America/Cuiaba') || tz.startsWith('America/Porto_Velho') ||
        tz.startsWith('America/Campo_Grande') || tz.startsWith('America/Rio_Branco') ||
        tz.startsWith('America/Maceio') || tz.startsWith('America/Araguaina') ||
        tz.startsWith('Europe/Lisbon') || tz.startsWith('Atlantic/Azores') ||
        tz.startsWith('Africa/Luanda') || tz.startsWith('Africa/Maputo')) {
      return 'BR'
    }
    return null
  } catch {
    return null
  }
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    // 1. Check localStorage
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved && TRANSLATIONS[saved]) return saved

    // 2. Check browser language
    const browserLang = navigator.language || navigator.languages?.[0] || ''
    if (browserLang.startsWith('pt')) return 'pt-BR'

    return 'en'
  })

  useEffect(() => {
    // If no saved preference, try IP-based detection
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) return // User already chose — respect it

    detectCountry().then(country => {
      if (!country) return
      if (PT_COUNTRIES.includes(country) && locale !== 'pt-BR') {
        setLocaleState('pt-BR')
      }
      // Don't override if already English — it's the default
    })
  }, [])

  const setLocale = useCallback((newLocale) => {
    if (TRANSLATIONS[newLocale]) {
      setLocaleState(newLocale)
      localStorage.setItem(STORAGE_KEY, newLocale)
    }
  }, [])

  const t = useCallback((key, params) => {
    const value = getNestedValue(TRANSLATIONS[locale], key)
      || getNestedValue(TRANSLATIONS['en'], key) // fallback to English
      || key // fallback to key itself

    if (!params) return value

    // Simple template replacement: {{variable}}
    return value.replace(/\{\{(\w+)\}\}/g, (_, k) => params[k] ?? '')
  }, [locale])

  const value = {
    locale,
    setLocale,
    t,
    isPortuguese: locale === 'pt-BR',
    isEnglish: locale === 'en',
  }

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n() {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}
